import { router } from '@inertiajs/react'
import { Palette } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Switch } from '@/components/ui/Switch'
import { useRouteParams } from '@/hooks/useRoutes'
import { useBooleanEntitlement } from '@/hooks/useEntitlement'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import type { EmailBrandingSettings as EmailBranding } from '@/pages/Settings.types'

interface EmailBrandingSettingsProps {
  branding: EmailBranding
}

const FOOTER_MAX = 200

const COLOR_FIELDS: { key: keyof EmailBranding; label: string; hint: string }[] = [
  { key: 'primary_color', label: 'Accent color', hint: 'Buttons and the top accent bar' },
  { key: 'header_bg_color', label: 'Header background', hint: 'The bar behind your logo' },
  { key: 'header_text_color', label: 'Header text', hint: 'Your business name in the header' },
]

export function EmailBrandingSettings({ branding }: EmailBrandingSettingsProps) {
  const { withAccount } = useRouteParams()
  const canRemoveBranding = useBooleanEntitlement('email_remove_branding').enabled
  const [form, setForm] = useState<EmailBranding>(branding)
  const [saving, setSaving] = useState(false)

  const update = <K extends keyof EmailBranding>(key: K, value: EmailBranding[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const save = () => {
    setSaving(true)
    router.patch(
      routes.settings.updateEmailBranding.path(withAccount()),
      {
        email_branding: {
          primary_color: form.primary_color,
          header_bg_color: form.header_bg_color,
          header_text_color: form.header_text_color,
          footer_text: form.footer_text ?? '',
          show_powered_by: form.show_powered_by,
        },
        ...activeBrandParam(),
      },
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Email branding saved'),
        onError: (errors) => 'authorization' in errors || toast.error(Object.values(errors)[0] ?? 'Could not save branding'),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="size-5" />
          Email branding
        </CardTitle>
        <CardDescription>
          Brand the emails this brand's visitors receive (follow-ups, replies, transcripts and
          feedback requests). The brand logo and shared sender address are already applied — set the
          colors and footer below, then use the test card to preview.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {COLOR_FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={`email-${key}`}>{label}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id={`email-${key}`}
                  value={(form[key] as string) || '#000000'}
                  onChange={(e) => update(key, e.target.value)}
                  className="h-10 w-12 shrink-0 rounded border border-border cursor-pointer"
                />
                <Input
                  value={(form[key] as string) ?? ''}
                  onChange={(e) => update(key, e.target.value)}
                  className="font-mono"
                  placeholder="#10B981"
                />
              </div>
              <p className="text-xs text-muted-foreground">{hint}</p>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email-footer-text">Footer text</Label>
          <Input
            id="email-footer-text"
            value={form.footer_text ?? ''}
            maxLength={FOOTER_MAX}
            onChange={(e) => update('footer_text', e.target.value)}
            placeholder="e.g. Acme Support · support@acme.com · Mon–Fri 9–5"
          />
          <p className="text-xs text-muted-foreground">
            Replaces the default footer line. Leave blank to keep the default.{' '}
            {(form.footer_text ?? '').length}/{FOOTER_MAX}
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-3">
          <div>
            <Label htmlFor="email-powered-by">Show &ldquo;Powered by Banshee&rdquo;</Label>
            <p className="text-xs text-muted-foreground mt-1">
              {canRemoveBranding
                ? 'Turn off to remove the Banshee link from your email footers.'
                : 'Removing the Banshee link is available on higher plans.'}
            </p>
          </div>
          <Switch
            id="email-powered-by"
            checked={canRemoveBranding ? form.show_powered_by : true}
            disabled={!canRemoveBranding}
            onCheckedChange={(v) => update('show_powered_by', v)}
            className="cursor-pointer"
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            Save, then use <strong className="text-foreground">Email Templates</strong> below to
            send yourself a test and see your branding live.
          </p>
          <Button onClick={save} disabled={saving} className="cursor-pointer">
            {saving ? 'Saving…' : 'Save branding'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
